import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { PendingWorkProvider } from './contexts/PendingWorkContext';

// Components
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import CheckStatus from './pages/CheckStatus';
import Dashboard from './pages/Dashboard';
import SalaryDetail from './pages/SalaryDetail';
import YearlySummary from './pages/YearlySummary';
// PrintSlip ถูกลบออก - user ต้องขอผ่านระบบคำร้องแทน
import Profile from './pages/Profile';
import Reviews from './pages/Reviews';
import CoopSavings from './pages/CoopSavings';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AccountRequests from './pages/admin/AccountRequests';
import PasswordResets from './pages/admin/PasswordResets';
import PositionRequests from './pages/admin/PositionRequests';
import AdminStaff from './pages/admin/AdminStaff';
import AdminSettings from './pages/admin/AdminSettings';
import AdminReportSettings from './pages/admin/AdminReportSettings';
import AdminChat from './pages/admin/AdminChat';
import AdminRequests from './pages/admin/AdminRequests';
import AdminRoles from './pages/admin/AdminRoles';
import AdminActivityLogs from './pages/admin/AdminActivityLogs';
import AdminPrintSlip from './pages/admin/AdminPrintSlip';
import AdminPrint50Tawi from './pages/admin/AdminPrint50Tawi';
import AdminPrintHistory from './pages/admin/AdminPrintHistory';
import AdminSignatures from './pages/admin/AdminSignatures';
import AdminMaintenance from './pages/admin/AdminMaintenance';
import AdminUserView from './pages/admin/AdminUserView';
import AdminReports from './pages/admin/AdminReports';
import AdminUsageReport from './pages/admin/AdminUsageReport';
import AdminShareLogs from './pages/admin/AdminShareLogs';
import AdminCoopSavings from './pages/admin/AdminCoopSavings';
import AdminContact from './pages/admin/AdminContact';
import AdminPaymentDates from './pages/admin/AdminPaymentDates';
import AdminBannedCids from './pages/admin/AdminBannedCids';
import DocumentRequest from './pages/DocumentRequest';
import VerifyDocument from './pages/VerifyDocument';
import MaintenancePage from './pages/MaintenancePage';
import BannedPage from './pages/BannedPage';

// เด้งไปหน้า login พร้อมจำหน้าที่ตั้งใจจะเข้า (เช่นกดลิงก์จาก LINE -> /admin/account-requests)
// Login จะพากลับมาที่หน้านี้หลังเข้าสู่ระบบ แทนที่จะทิ้งไว้ที่หน้าแรกจนไม่รู้ว่าต้องไปไหนต่อ
function RedirectToLogin() {
    const location = useLocation();
    const from = location.pathname + location.search + location.hash;
    return <Navigate to="/login" replace state={from && from !== '/' ? { from } : undefined} />;
}

// Protected Route Component with Maintenance Check
function ProtectedRoute({ children, requiredPermissions = [] }) {
    const { user, loading, hasAnyPermission, isAdmin, maintenanceMode, maintenanceInfo, checkMaintenance } = useAuth();

    if (loading) {
        return (
            <div className="flex-center" style={{ height: '100vh' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (!user) {
        return <RedirectToLogin />;
    }

    // ตรวจสอบ Maintenance Mode - Admin ไม่ได้รับผลกระทบ
    if (maintenanceMode && !isAdmin()) {
        return (
            <MaintenancePage
                message={maintenanceInfo?.message}
                endTime={maintenanceInfo?.end_time}
                onRetry={checkMaintenance}
            />
        );
    }

    // ถ้าต้องการ permission เฉพาะ
    if (requiredPermissions.length > 0) {
        // Admin ผ่านได้ทุกหน้า
        if (isAdmin()) {
            return children;
        }
        // ตรวจสอบ permission
        if (!hasAnyPermission(requiredPermissions)) {
            return <Navigate to="/" replace />;
        }
    }

    return children;
}

// Smart Home - เจ้าหน้าที่ทุก role ที่ไม่ใช่ user ทั่วไป -> หน้า HOME = /admin
function SmartHome() {
    const { canAccessAdmin } = useAuth();

    // เจ้าหน้าที่ (admin / การเงิน / role อื่นที่ได้รับสิทธิ์) -> หน้าจัดการระบบ
    // ปุ่ม/เมนูภายในจะแสดงตามสิทธิ์ที่ได้รับ (ผ่าน <Can>)
    if (canAccessAdmin()) {
        return <Navigate to="/admin" replace />;
    }

    // พนักงานทั่วไป (user) -> Dashboard ดูเงินเดือนตัวเอง
    return <Dashboard />;
}

// Admin Role Route - เฉพาะ role admin/super_admin เท่านั้น (เจ้าหน้าที่ที่ได้ permission รายหน้าไม่ผ่าน)
function AdminRoleRoute({ children }) {
    const { user, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="flex-center" style={{ height: '100vh' }}>
                <div className="spinner"></div>
            </div>
        );
    }
    if (!user) return <RedirectToLogin />;
    if (!isAdmin()) return <Navigate to="/admin" replace />;
    return children;
}

// Admin Route - ต้องเป็น admin หรือมี admin permissions
function AdminRoute({ children, requiredPermissions = [] }) {
    const { user, loading, canAccessAdmin, hasAnyPermission, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="flex-center" style={{ height: '100vh' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (!user) {
        return <RedirectToLogin />;
    }

    // Admin ผ่านได้ทุกหน้า
    if (isAdmin()) {
        return children;
    }

    // ถ้ามี requiredPermissions ให้ตรวจสอบเฉพาะ permissions นั้น
    if (requiredPermissions.length > 0) {
        if (hasAnyPermission(requiredPermissions)) {
            return children;
        }
    } else {
        // ถ้าไม่ได้ระบุ permission เฉพาะ ให้ตรวจสอบว่าเข้าถึง admin panel ได้ไหม
        if (canAccessAdmin()) {
            return children;
        }
    }

    return <Navigate to="/" replace />;
}

// Banned Page Route
function BannedRoute() {
    const banInfo = localStorage.getItem('banInfo');
    const parsedBanInfo = banInfo ? JSON.parse(banInfo) : null;

    const handleRetry = () => {
        localStorage.removeItem('banInfo');
        window.location.href = '/';
    };

    if (!parsedBanInfo && !localStorage.getItem('token')) {
        return <Navigate to="/login" replace />;
    }

    return (
        <BannedPage
            message={parsedBanInfo?.message}
            bannedUntil={parsedBanInfo?.banned_until}
            onRetry={handleRetry}
        />
    );
}

function App() {
    return (
        <>
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/check-status" element={<CheckStatus />} />
            <Route path="/verify" element={<VerifyDocument />} />
            <Route path="/banned" element={<BannedRoute />} />

            {/* Protected Routes */}
            <Route path="/" element={
                <ProtectedRoute>
                    {/* งานรอดำเนินการของเจ้าหน้าที่ — ใช้ร่วมกันทั้ง badge เมนู แถบบอกทาง และ AdminDashboard */}
                    <PendingWorkProvider>
                        <Layout />
                    </PendingWorkProvider>
                </ProtectedRoute>
            }>
                <Route index element={<SmartHome />} />
                <Route path="salary/:yyyy/:mm" element={<SalaryDetail />} />
                <Route path="yearly/:yyyy" element={<YearlySummary />} />
                {/* Route /print ถูกลบ - redirect ไป /request แทน */}
                <Route path="print" element={<Navigate to="/request" replace />} />
                <Route path="request" element={<DocumentRequest />} />
                <Route path="profile" element={<Profile />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="coop" element={<CoopSavings />} />

                {/* Admin Routes */}
                <Route path="admin" element={
                    <AdminRoute>
                        <AdminDashboard />
                    </AdminRoute>
                } />
                <Route path="admin/print-history" element={
                    <AdminRoute>
                        <AdminPrintHistory />
                    </AdminRoute>
                } />
                <Route path="admin/user-view/:userId" element={
                    <AdminRoute>
                        <AdminUserView />
                    </AdminRoute>
                } />
                <Route path="admin/users" element={
                    <AdminRoute requiredPermissions={['users.view', 'users.edit']}>
                        <AdminUsers />
                    </AdminRoute>
                } />
                <Route path="admin/account-requests" element={
                    <AdminRoute requiredPermissions={['account_request.manage']}>
                        <AccountRequests />
                    </AdminRoute>
                } />
                <Route path="admin/password-resets" element={
                    <AdminRoute requiredPermissions={['account_request.manage']}>
                        <PasswordResets />
                    </AdminRoute>
                } />
                <Route path="admin/position-requests" element={
                    <AdminRoute requiredPermissions={['position_request.manage', 'users.view', 'users.edit']}>
                        <PositionRequests />
                    </AdminRoute>
                } />
                <Route path="admin/staff" element={
                    <AdminRoute requiredPermissions={['users.view', 'users.edit']}>
                        <AdminStaff />
                    </AdminRoute>
                } />
                <Route path="admin/settings" element={
                    <AdminRoute requiredPermissions={['settings.view', 'settings.edit']}>
                        <AdminSettings />
                    </AdminRoute>
                } />
                <Route path="admin/report-settings" element={
                    <AdminRoute requiredPermissions={['settings.report', 'settings.edit']}>
                        <AdminReportSettings />
                    </AdminRoute>
                } />
                <Route path="admin/chat" element={
                    <AdminRoute requiredPermissions={['chat.admin', 'chat.view_all']}>
                        <AdminChat />
                    </AdminRoute>
                } />
                <Route path="admin/requests" element={
                    <AdminRoute requiredPermissions={['request.view_all', 'request.process', 'request.approve']}>
                        <AdminRequests />
                    </AdminRoute>
                } />
                <Route path="admin/roles" element={
                    <AdminRoute requiredPermissions={['roles.view', 'roles.edit']}>
                        <AdminRoles />
                    </AdminRoute>
                } />
                <Route path="admin/activity-logs" element={
                    <AdminRoute requiredPermissions={['system.logs']}>
                        <AdminActivityLogs />
                    </AdminRoute>
                } />
                <Route path="admin/print-slip" element={
                    <AdminRoute requiredPermissions={['request.view_all', 'request.process', 'request.approve']}>
                        <AdminPrintSlip />
                    </AdminRoute>
                } />
                <Route path="admin/print-50tawi" element={
                    <AdminRoute requiredPermissions={['request.view_all', 'request.process', 'request.approve']}>
                        <AdminPrint50Tawi />
                    </AdminRoute>
                } />
                <Route path="admin/signatures" element={
                    <AdminRoute requiredPermissions={['settings.edit']}>
                        <AdminSignatures />
                    </AdminRoute>
                } />
                <Route path="admin/reports" element={
                    <AdminRoute requiredPermissions={['view_reports', 'admin_access']}>
                        <AdminReports />
                    </AdminRoute>
                } />
                <Route path="admin/usage-report" element={
                    <AdminRoute requiredPermissions={['view_reports', 'admin_access']}>
                        <AdminUsageReport />
                    </AdminRoute>
                } />
                <Route path="admin/coop-savings" element={
                    <AdminRoleRoute>
                        <AdminCoopSavings />
                    </AdminRoleRoute>
                } />
                <Route path="admin/share-logs" element={
                    <AdminRoute requiredPermissions={['view_reports', 'admin_access']}>
                        <AdminShareLogs />
                    </AdminRoute>
                } />
                <Route path="admin/contact" element={
                    <AdminRoute requiredPermissions={['request.view_all', 'request.process', 'users.view']}>
                        <AdminContact />
                    </AdminRoute>
                } />
                <Route path="admin/maintenance" element={
                    <AdminRoute requiredPermissions={['settings.edit', 'system.maintenance']}>
                        <AdminMaintenance />
                    </AdminRoute>
                } />
                <Route path="admin/payment-dates" element={
                    <AdminRoute requiredPermissions={['settings.edit', 'request.process']}>
                        <AdminPaymentDates />
                    </AdminRoute>
                } />
                {/* บัญชีดำ — สิทธิ์รายคนเท่านั้น (ตัว component เช็ค banned_cid.manage ซ้ำอีกชั้น กัน admin ทั่วไป) */}
                <Route path="admin/banned-cids" element={
                    <AdminRoute requiredPermissions={['banned_cid.manage']}>
                        <AdminBannedCids />
                    </AdminRoute>
                } />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

export default App;
